// src/features/messages/ConversationFilters.tsx — All/Unread/Requests/Groups chips.
import styled from 'styled-components';
import { FILTERS } from './meta';
import type { ConvoFilter } from './meta';

const Row = styled.div`display: flex; gap: 6px; padding: 0 4px 4px;`;
const Chip = styled.button<{ $active: boolean }>`
  cursor: pointer; padding: 5px 12px; border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  border: 1px solid ${({ theme, $active }) => ($active ? 'transparent' : theme.color.glassBorder)};
  color: ${({ theme, $active }) => ($active ? theme.color.onAccent : theme.color.textPrimary)};
  background: ${({ theme, $active }) => ($active ? theme.color.accentCyan : theme.color.glassBg)};
  &:hover { filter: brightness(1.12); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.accentCyan}; outline-offset: 2px; }
`;

const Count = styled.span`
  margin-left: 6px;
  display: inline-grid;
  place-items: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700;
  color: ${({ theme }) => theme.color.onAccent};
  background: ${({ theme }) => theme.color.accentCyan};
`;

// The count chip inverts when its own tab is active (the chip is already cyan
// there, so a cyan pill on cyan would vanish).
const CountOnActive = styled(Count)`
  color: ${({ theme }) => theme.color.accentCyan};
  background: ${({ theme }) => theme.color.onAccent};
`;

export interface ConversationFiltersProps {
  active: ConvoFilter;
  onChange: (filter: ConvoFilter) => void;
  /** How many threads await acceptance — badges the Requests chip. Default 0. */
  requestsCount?: number;
}

export function ConversationFilters({
  active,
  onChange,
  requestsCount = 0,
}: ConversationFiltersProps) {
  return (
    <Row role="group" aria-label="Conversation filters">
      {FILTERS.map((f) => {
        const isActive = active === f.id;
        const showCount = f.id === 'requests' && requestsCount > 0;
        const Pill = isActive ? CountOnActive : Count;
        return (
          <Chip
            key={f.id}
            type="button"
            $active={isActive}
            aria-pressed={isActive}
            // The count is inside the button, so without an explicit label the
            // accessible name would read "Requests 3" — spell it out instead.
            aria-label={showCount ? `${f.label} (${requestsCount})` : undefined}
            onClick={() => onChange(f.id)}
          >
            {f.label}
            {showCount && <Pill aria-hidden="true">{requestsCount}</Pill>}
          </Chip>
        );
      })}
    </Row>
  );
}
